import React, { Children, useContext, useMemo } from 'react';

// DEPENDENCIES
import partition from 'lodash/partition';
import sumBy from 'lodash/sumBy';
import { useFormikContext } from 'formik';

// GLOBAL CONTEXT
import { RecordContext } from 'helpers/getRecordData';

// FUNCTIONS/HELPERS
import useRecordView from 'helpers/getRecordView';

// CONTEXT
const AMFIProductsContext = React.createContext({});

// MAIN COMPONENT
export default function AMFIProductsProvider({ children, readOnly }) {
  const { values } = useFormikContext() || {};
  const { record } = useContext(RecordContext) || {};
  const { isSales } = useRecordView();

  const { am_gross_time, fm_gross_time } = useMemo(() => record || {}, [record]);

  const { aryDealerAMFinance, am_gross = 0, fm_gross = 0 } = useMemo(() => values || {}, [values]);

  // NOTE: aryDealerAMFinance initialValues should be sorted by product_type ascending
  const [amProducts = [], fiProducts = []] = useMemo(
    () =>
      isSales
        ? partition(aryDealerAMFinance || [], { product_type: 0 })
        : aryDealerAMFinance.reduce((arr, a) => {
            if (a.product_type === 0) arr[0] = [...(arr[0] || []), a];
            if (a.product_type === 1) arr[1] = [...(arr[1] || []), a];
            return arr;
          }, []),
    [aryDealerAMFinance, isSales]
  );

  const amProductsTotalGross = useMemo(() => {
    return sumBy(amProducts || [], p => +p?.item_gross);
  }, [amProducts]);

  const fiProductsTotalGross = useMemo(() => {
    return sumBy(fiProducts || [], p => +p?.item_selling_price || +p.item_gross);
  }, [fiProducts]);

  const totalBackendGross = useMemo(() => {
    const amTotal = amProducts.length ? amProductsTotalGross : +am_gross;
    const fiTotal = fiProducts.length ? fiProductsTotalGross : +fm_gross;
    return amTotal + fiTotal;
  }, [amProducts, fiProducts, amProductsTotalGross, fiProductsTotalGross, am_gross, fm_gross]);

  // RENDER
  return (
    <AMFIProductsContext.Provider
      value={{
        amGrossTime: am_gross_time,
        amProducts,
        amProductsTotalGross,
        aryDealerAMFinance,
        fiProducts,
        fiProductsTotalGross,
        fmGrossTime: fm_gross_time,
        totalBackendGross,
        readOnly
      }}
    >
      {Children.only(children)}
    </AMFIProductsContext.Provider>
  );
}

// HOOK
export const useAMFIProducts = () => useContext(AMFIProductsContext);
